import VoltarLink from "@/components/VoltarLink";
import TextoRecolhivel from "@/components/TextoRecolhivel";
import PublicacaoCompleta from "@/components/PublicacaoCompleta";
import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import AgendaProcesso from "@/components/AgendaProcesso";
import { http, registrarDonos } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BannerComo from "@/components/BannerComo";
import ResponsavelProcesso from "@/components/ResponsavelProcesso";
import { BadgeParticular, marcarParticular } from "@/components/Particular";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, Sparkles, ArrowLeft, Plus, History, Trash2, Save, RefreshCw, ClipboardPaste, Pencil, MoreHorizontal, Archive, ArchiveRestore, ChevronDown, ChevronRight, Users, Copy, Check, Link2, Clock3 } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import NewEventDialog from "@/components/NewEventDialog";
import { maskDateInput, dateBrToIso, isoToDateBr, maskCurrencyInput } from "@/lib/dateTimeMask";
import { formatDate, toTitleCasePt } from "@/lib/format";
import { CASE_AREAS, PARTY_LABELS, KNOWN_CASE_AREAS } from "@/lib/caseAreas";
import AcompanhamentoProcesso from "@/components/AcompanhamentoProcesso";

import { Dica } from "@/components/Dica";
import AutosTag, { autosLabel, autosMeta } from "@/components/AutosTag";
import AutosDialog from "@/components/AutosDialog";
import { useGaveta, useContextoAssistente } from "@/context/GavetaContext";
import FilaClassificar from "@/components/FilaClassificar";
// Tela de detalhe do processo (Lote 44, pedido do Robson — proposta enviada e aprovada em
// chat: "Sugira a área interna dessa aba"). Cadastro básico ampliado (comarca/vara,
// partes, valor da causa, data de ajuizamento) + histórico de andamentos, alimentado
// automaticamente por publicações do DJEN e movimentações do DataJud que casam com o
// número do processo (ver `_route_publication_to_historico` no backend), mais um botão
// pra registrar andamentos manuais.
//
// Lote novo (02/10/2026, pedido do Robson): Comarca e Vara passaram a ser dois campos
// separados (igual ao padrão já usado nas audiências — court/court_district), em vez do
// campo único "Comarca / Vara" de antes (comarca_vara) — ver migração de backfill no
// backend (_migrate_processo_comarca_vara). "Tipo de ação" e os rótulos de parte agora
// vêm de @/lib/caseAreas (compartilhado com NewEventDialog.jsx e Processos.jsx), que
// ganhou "Previdenciário" e o mesmo campo de texto livre pra "Outro" que já existia no
// "Novo evento" (case_area_custom).

// Lote 120 (Robson, 25/10/2026) — origem discreta: texto cinza pequeno com uma bolinha de cor suave (sem etiqueta colorida);
// o que o próprio app registrou (Interno/Sistema) fica ainda mais apagado que o que veio do tribunal.
const ORIGEM_LABEL = {
  manual: { label: "Manual", dot: "bg-stone-400", text: "text-stone-500" },
  djen: { label: "DJEN", dot: "bg-rose-300", text: "text-stone-500" },
  datajud: { label: "DataJud", dot: "bg-sky-300", text: "text-stone-500" },
  // Lote 82 — andamento colado do e-mail do tribunal (PUSH); o rótulo mostra o sistema (e-SAJ, eproc…).
  push: { label: "PUSH", dot: "bg-emerald-300", text: "text-stone-500" },
  colado: { label: "Colado", dot: "bg-amber-300", text: "text-stone-500" },
  // Lote 107 — ações do próprio advogado no app (agendou, baixou, redesignou, revisou…).
  sistema: { label: "Sistema", dot: "bg-stone-300", text: "text-stone-400" },
  acao: { label: "Interno", dot: "bg-gold-300", text: "text-stone-400" },
  // Lote 93 — registro de "Compartilhar" (fica numa faixa expansível, fora da linha do tempo principal).
  compartilhado: { label: "Compartilhado", dot: "bg-sky-300", text: "text-stone-500" },
};

// Lote 120 — "hoje" / "ontem" / "há N dias" / "há N meses" ao lado da data de cada bolinha.
const ISO_HOJE = () => { const d = new Date(); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
export function rotuloRelativo(iso, hojeIso = ISO_HOJE()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  const h = /^(\d{4})-(\d{2})-(\d{2})/.exec(hojeIso);
  if (!m || !h) return "";
  const n = Math.round((Date.UTC(+h[1], +h[2] - 1, +h[3]) - Date.UTC(+m[1], +m[2] - 1, +m[3])) / 86400000);
  if (n === 0) return "hoje";
  if (n === 1) return "ontem";
  if (n === -1) return "amanhã";
  if (n < 0) return `em ${-n} dias`;
  if (n < 45) return `há ${n} dias`;
  const meses = Math.round(n / 30.4);
  if (meses < 12) return `há ${meses} ${meses === 1 ? "mês" : "meses"}`;
  const anos = Math.floor(n / 365);
  return `há ${anos} ${anos === 1 ? "ano" : "anos"}`;
}

export default function ProcessoDetail() {
  const { id } = useParams();
  const gaveta = useGaveta();
  const navigate = useNavigate();
  const { user: eu } = useAuth() || {};
  const { search: buscaUrl } = useLocation();
  const deDono = new URLSearchParams(buscaUrl).get("de");
  // Lote 157: processo de um colega — lembra o dono (ex.: a exclusão adiada roda depois que a tela fecha)
  useEffect(() => { if (deDono) registrarDonos([{ processo_id: id, dono_id: deDono }], eu?.user_id, "processo_id"); }, [deDono, id, eu?.user_id]);
  const [numeroOpen, setNumeroOpen] = useState(false);
  const [novoNumero, setNovoNumero] = useState("");
  const [salvandoNumero, setSalvandoNumero] = useState(false);
  const [processo, setProcesso] = useState(null);
  useContextoAssistente(processo ? { tipo: "processo", id: processo.processo_id || id, rotulo: processo.apelido || processo.numero } : null);   // Lote 154
  const [clients, setClients] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [sharesOpen, setSharesOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Formulário — carregado do processo assim que chega, editado localmente até "Salvar".
  const [apelido, setApelido] = useState("");
  const [clientId, setClientId] = useState("none");
  const [comarca, setComarca] = useState("");
  const [vara, setVara] = useState("");
  const [caseArea, setCaseArea] = useState("outro");
  const [caseAreaCustom, setCaseAreaCustom] = useState("");
  const [partyAuthor, setPartyAuthor] = useState("");
  const [partyDefendant, setPartyDefendant] = useState("");
  const [valorCausa, setValorCausa] = useState("");
  const [dataAjuizamentoText, setDataAjuizamentoText] = useState("");
  // Lote 84: "Dados do processo" abre resumido (só o que está preenchido); "Editar" mostra o formulário completo.
  const [editando, setEditando] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addDataText, setAddDataText] = useState("");
  const [addDescricao, setAddDescricao] = useState("");
  const [adding, setAdding] = useState(false);

  // Lote 82 — "Colar andamento": cola o e-mail do PUSH (e-SAJ/eproc/Projudi/PJe), vê a prévia, confirma.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pastePreview, setPastePreview] = useState(null);
  const [pasteBusy, setPasteBusy] = useState(false);
  const [filtroHist, setFiltroHist] = useState("tudo"); // Lote 107: tudo | externo | interno
  const [showHidden, setShowHidden] = useState(false); // andamentos do DataJud ocultos pelo PUSH
  const [agendarOpen, setAgendarOpen] = useState(false); // Lote 120: "+ Agendar" dentro do processo
  const [agendarPrefill, setAgendarPrefill] = useState(null); // objeto estável (o diálogo reinicia o formulário se ele mudar)
  // Lote 117: limpar andamentos (excesso / lançados errado) — modo de seleção + desfazer.
  // Lote 118: vários autos com o mesmo número.
  const [autosDlg, setAutosDlg] = useState(null);       // null | "novo" | "editar"
  const [addAlvo, setAddAlvo] = useState("");           // autos que vai receber o andamento manual ("" = este)
  const [selMode, setSelMode] = useState(false);
  const [selIds, setSelIds] = useState(() => new Set());
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Lote 168 — ficha em abas (Visão geral / Histórico / Agenda), copiar número, confirmação de exclusão, "Documentos e vínculos".
  const dadosRef = useRef(null);
  const [copiado, setCopiado] = useState(false);
  const [excluirOpen, setExcluirOpen] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [vincOpen, setVincOpen] = useState(false);
  const load = async () => {
    const [p, c, h] = await Promise.all([
      http.get(`/processos/${id}`),
      http.get("/clients"),
      http.get(`/processos/${id}/historico`),
    ]);
    setProcesso(p.data);
    setClients(c.data);
    setHistorico(h.data);
    fillForm(p.data);
  };
  const fillForm = (pd) => {
    const p = { data: pd };
    setApelido(p.data.apelido || "");
    setClientId(p.data.client_id || "none");
    setComarca(p.data.comarca || "");
    setVara(p.data.vara || "");
    // "Tipo de ação" com texto livre (mesmo padrão do "Novo evento", ver NewEventDialog.jsx):
    // um processo antigo pode ter case_area vindo como texto qualquer digitado antes (não
    // só as opções fixas) — nesse caso mostra "Outro" selecionado com o texto real preenchido.
    const rawCaseArea = p.data.case_area || "outro";
    const isKnownCaseArea = KNOWN_CASE_AREAS.includes(rawCaseArea);
    setCaseArea(isKnownCaseArea ? rawCaseArea : "outro");
    setCaseAreaCustom(isKnownCaseArea ? "" : (rawCaseArea === "outro" ? "" : rawCaseArea));
    setPartyAuthor(p.data.party_author || "");
    setPartyDefendant(p.data.party_defendant || "");
    setValorCausa(p.data.valor_causa || "");
    setDataAjuizamentoText(p.data.data_ajuizamento ? isoToDateBr(p.data.data_ajuizamento) : "");
  };
  useEffect(() => { setAddAlvo(""); load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      // Mesma lógica do "Novo evento" (NewEventDialog.jsx): quando a área é "Outro" e tem
      // texto digitado, é esse texto que vira o case_area de verdade.
      const resolvedCaseArea =
        caseArea === "outro" && caseAreaCustom.trim() ? caseAreaCustom.trim() : caseArea || null;
      const r = await http.put(`/processos/${id}`, {
        apelido: apelido.trim() || null,
        client_id: clientId === "none" ? null : clientId,
        comarca: comarca.trim() || null,
        vara: vara.trim() || null,
        case_area: resolvedCaseArea,
        party_author: partyAuthor.trim() || null,
        party_defendant: partyDefendant.trim() || null,
        valor_causa: valorCausa.trim() || null,
        data_ajuizamento: dataAjuizamentoText ? dateBrToIso(dataAjuizamentoText) || null : null,
      });
      setProcesso(r.data);
      setEditando(false);
      toast.success("Dados salvos");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  // Lote 76: consulta o DataJud só deste processo e recarrega o histórico.
  const [syncingOne, setSyncingOne] = useState(false);
  const [acompKey, setAcompKey] = useState(0);   // Lote 114: faz o bloco Acompanhamento recarregar
  const reloadHist = async () => {
    try {
      const h = await http.get(`/processos/${id}/historico`);
      setHistorico(h.data);
    } catch (e) { /* mantém o que está na tela */ }
  };
  const syncThis = async () => {
    setSyncingOne(true);
    try {
      const r = await http.post(`/processos/${id}/sync-datajud`);
      const n = r.data?.movements_imported || 0;
      if (r.data?.errors || r.data?.unsupported_alias) {
        toast.warning("Não consegui consultar o DataJud agora", { description: "Confira o número do processo ou tente de novo em instantes." });
      } else {
        toast.success("Histórico atualizado", { description: n > 0 ? `${n} movimentação(ões) nova(s).` : "Nenhuma movimentação nova." });
      }
      const h = await http.get(`/processos/${id}/historico`);
      setHistorico(h.data);
      setAcompKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
    } finally { setSyncingOne(false); }
  };

  const closePaste = () => { setPasteOpen(false); setPasteText(""); setPastePreview(null); };

  const readPaste = async () => {
    setPasteBusy(true);
    try {
      const r = await http.post(`/processos/${id}/colar-andamento`, { texto: pasteText });
      setPastePreview(r.data);
    } catch (e) {
      setPastePreview(null);
      toast.error(e?.response?.data?.detail || "Não consegui ler o texto");
    } finally { setPasteBusy(false); }
  };

  const confirmPaste = async (ignorarNumero = false) => {
    setPasteBusy(true);
    try {
      const r = await http.post(`/processos/${id}/colar-andamento`, { texto: pasteText, confirmar: true, ignorar_numero: ignorarNumero });
      const d = r.data || {};
      toast.success(d.adicionados ? `${d.adicionados} andamento(s) adicionado(s)` : "Nada novo para adicionar", {
        description: [
          d.duplicados ? `${d.duplicados} já estava(m) no histórico.` : null,
          d.ocultados_datajud ? `${d.ocultados_datajud} do DataJud ocultado(s) (mesmo ato no PUSH).` : null,
        ].filter(Boolean).join(" ") || undefined,
      });
      closePaste();
      const h = await http.get(`/processos/${id}/historico`);
      setHistorico(h.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao adicionar andamentos");
    } finally { setPasteBusy(false); }
  };

  const addAndamento = async () => {
    const dataIso = dateBrToIso(addDataText);
    if (!dataIso) {
      toast.error("Preencha a data no formato dd/mm/aaaa");
      return;
    }
    if (!addDescricao.trim()) {
      toast.error("Descreva o andamento");
      return;
    }
    setAdding(true);
    try {
      const alvo = addAlvo && addAlvo !== id ? addAlvo : id;
      await http.post(`/processos/${alvo}/historico`, { data: dataIso, descricao: addDescricao.trim() });
      const outro = alvo !== id ? (processo.irmaos || []).find((x) => x.processo_id === alvo) : null;
      toast.success(outro ? `Andamento adicionado em ${autosLabel(outro.autos, outro.autos_nome)}` : "Andamento adicionado");
      setAddOpen(false);
      setAddDataText("");
      setAddDescricao("");
      const h = await http.get(`/processos/${id}/historico`);
      setHistorico(h.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao adicionar andamento");
    } finally {
      setAdding(false);
    }
  };

  // Lote 117: exclui um ou vários andamentos (vão para uma lixeira de 60 dias; "Desfazer" devolve).
  const excluirAndamentos = async (ids) => {
    if (!ids.length) return;
    setDeleting(true);
    try {
      const r = await http.post(`/processos/${id}/historico/excluir`, { ids });
      const feitos = r.data?.ids || [];
      setHistorico((cur) => cur.filter((a) => !feitos.includes(a.andamento_id || a.acao_id)));
      setSelIds(new Set());
      setSelMode(false);
      setConfirmDel(false);
      if (!feitos.length) { toast.error("Nada foi excluído (esses itens não podem ser excluídos)"); return; }
      toast.success(feitos.length === 1 ? "Andamento excluído" : `${feitos.length} andamentos excluídos`, {
        duration: 8000,
        action: {
          label: "Desfazer",
          onClick: async () => {
            try {
              await http.post(`/processos/${id}/historico/restaurar`, { ids: feitos });
              const h = await http.get(`/processos/${id}/historico`);
              setHistorico(h.data);
              toast.success("Andamentos devolvidos ao histórico");
            } catch { toast.error("Não consegui desfazer"); }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    } finally {
      setDeleting(false);
    }
  };
  const delAndamento = (andamentoId) => excluirAndamentos([andamentoId]);
  // Lote 119: corrigir o texto/data de um andamento lançado à mão ou colado.
  const [editAnd, setEditAnd] = useState(null);   // { andamento_id, dataText, descricao }
  const [editBusy, setEditBusy] = useState(false);
  const abrirEdicao = (a) => setEditAnd({ andamento_id: a.andamento_id, dataText: isoToDateBr(a.data), descricao: a.descricao || "" });
  const salvarEdicao = async () => {
    const iso = dateBrToIso(editAnd.dataText);
    if (!iso) { toast.error("Preencha a data no formato dd/mm/aaaa"); return; }
    if (!editAnd.descricao.trim()) { toast.error("Descreva o andamento"); return; }
    setEditBusy(true);
    try {
      const r = await http.put(`/processos/${id}/historico/${editAnd.andamento_id}`, { data: iso, descricao: editAnd.descricao.trim() });
      setHistorico((cur) => cur.map((a) => (a.andamento_id === editAnd.andamento_id ? { ...a, ...r.data } : a)));
      setEditAnd(null);
      toast.success("Andamento corrigido");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); } finally { setEditBusy(false); }
  };
  const toggleSel = (aid) => setSelIds((cur) => { const n = new Set(cur); if (n.has(aid)) n.delete(aid); else n.add(aid); return n; });
  const sairSelecao = () => { setSelMode(false); setSelIds(new Set()); };

  const visiveisHist = historico.filter((a) => a.origem !== "compartilhado" && (showHidden || !a.oculto_por_push)
    && (filtroHist === "tudo"
      || (filtroHist === "revisoes" && (a.tipo === "revisado" || a.tipo === "reagendado" || a.tipo === "revisao_modo"))
      || (filtroHist === "interno" && a.origem === "acao" && !a.sistema)
      || (filtroHist === "externo" && a.origem !== "acao")));
  // Lote 119: também os registros internos/do sistema (ficam ocultos só neste autos; a Agenda não muda)
  const idDe = (a) => a.andamento_id || a.acao_id;
  const ehExcluivel = (a) => (["manual", "colado", "push", "datajud", "djen"].includes(a.origem) && !!a.andamento_id) || (a.origem === "acao" && !!a.acao_id);
  const excluiveis = visiveisHist.filter(ehExcluivel);
  // Lote 120: agrupa por dia (a ordem do backend é mantida — mais recente primeiro)
  const gruposHist = (() => {
    const m = new Map();
    visiveisHist.forEach((a) => { const k = String(a.data || "").slice(0, 10); if (!m.has(k)) m.set(k, []); m.get(k).push(a); });
    return [...m.entries()].sort((x, y) => (x[0] < y[0] ? 1 : x[0] > y[0] ? -1 : 0));
  })();

  if (!processo) return <div className="p-12 text-stone-500">Carregando…</div>;

  const abrirAgendar = () => setAgendarPrefill({
    case_number: processo.numero,
    autos_processo_id: (processo.irmaos || []).length > 0 ? id : "",
    court: processo.vara || "", court_district: processo.comarca || "",
    client_id: processo.client_id || "",
    party_author: processo.party_author || "", party_defendant: processo.party_defendant || "",
    case_area: KNOWN_CASE_AREAS.includes(processo.case_area) ? processo.case_area : "",
  }) || setAgendarOpen(true);

  const partyLabels = PARTY_LABELS[caseArea] || PARTY_LABELS.outro;

  // Lote 84 — resumo: só campos preenchidos (valores salvos no processo, não o rascunho do formulário).
  const rawArea = processo.case_area && processo.case_area !== "outro" ? processo.case_area : "";
  const areaLabel = rawArea ? (CASE_AREAS.find((a) => a.value === rawArea)?.label || rawArea) : "";
  const resumoLabels = PARTY_LABELS[KNOWN_CASE_AREAS.includes(processo.case_area) ? processo.case_area : "outro"] || PARTY_LABELS.outro;
  const clienteNome = processo.client_id ? clients.find((c) => c.client_id === processo.client_id)?.name : "";
  // Nome em CAIXA ALTA (vem assim do DJEN/DataJud) vira Título; texto já com minúsculas é mantido.
  // Siglas (sem vogal, ex. MPF, TRF, ou INSS/CEF) continuam em caixa alta; o app não consegue devolver acentos que o tribunal tirou.
  const SIGLAS = new Set(["INSS", "CEF", "EPP"]);
  const nomeBonito = (t) => {
    if (!t || t !== t.toUpperCase()) return t;
    const orig = t.split(/(\s+)/);
    const bonito = toTitleCasePt(t).split(/(\s+)/);
    return orig.map((w, i) => (/^[^AEIOUÁÉÍÓÚÂÊÔÃÕ\d\s]+$/i.test(w) || SIGLAS.has(w) ? w : bonito[i])).join("");
  };
  const resumoCampos = [
    { key: "apelido", label: "Apelido", value: processo.apelido },
    { key: "cliente", label: "Cliente", value: clienteNome },
    { key: "tipo", label: "Tipo de ação", value: areaLabel },
    { key: "comarca", label: "Comarca", value: processo.comarca },
    { key: "vara", label: "Vara", value: processo.vara },
    { key: "autor", label: resumoLabels[0], value: processo.party_author },
    { key: "reu", label: resumoLabels[1], value: processo.party_defendant },
    { key: "valor", label: "Valor da causa", value: processo.valor_causa },
    { key: "ajuiz", label: "Ajuizamento", value: processo.data_ajuizamento ? formatDate(processo.data_ajuizamento) : "" },
  ];
  const faltando = resumoCampos.filter((f) => !f.value);
  // Lote 168 — título da ficha: o cliente; sem cliente, as partes; o apelido fica junto do número.
  const partesTxt = [processo.party_author, processo.party_defendant].filter(Boolean).map((t) => nomeBonito(t)).join(" × ");
  const tituloFicha = clienteNome ? nomeBonito(clienteNome) : (partesTxt || processo.apelido || processo.numero);
  const editarDados = () => {
    setEditando(true);
    setTimeout(() => dadosRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };
  const salvarNumero = async () => {
    setSalvandoNumero(true);
    try {
      const r = await http.post(`/processos/${id}/numero`, { numero: novoNumero });
      setProcesso(r.data.processo);
      setNumeroOpen(false);
      const m = r.data.movidos || {};
      const n = (m.publicacoes || 0) + (m.caixa || 0);
      toast.success(n > 0 ? `Número alterado — ${n} publicaç${n > 1 ? "ões" : "ão"} acompanharam o novo número` : "Número alterado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao alterar o número");
    } finally {
      setSalvandoNumero(false);
    }
  };
  // Excluir: confirma e só apaga de verdade depois de 6 s (tempo do "Desfazer").
  // Lote 132b (Robson, 27/10/2026): "Arquivado" e "Extinto" têm que existir dentro do processo; ao marcar, a
  // pessoa é levada para a aba "Arquivados e extintos" de Processos. Reativar fica no próprio processo.
  const mudarSituacao = async (value, motivo = "arquivado") => {
    const pid = id;
    try {
      await http.post(`/processos/${pid}/arquivar`, { value, motivo });
    } catch { toast.error("Não consegui salvar. Tente de novo."); return; }
    if (value) {
      toast.success(motivo === "extinto" ? "Processo marcado como extinto" : "Processo arquivado", {
        description: "A consulta ao DataJud/DJEN foi pausada. Ele está em “Arquivados e extintos”.",
        duration: 6000,
        action: { label: "Desfazer", onClick: async () => {
          try { await http.post(`/processos/${pid}/arquivar`, { value: false }); toast.success("Processo reativado"); navigate(`/processos/${pid}`); }
          catch { toast.error("Não consegui desfazer"); }
        } },
      });
      navigate("/processos?aba=arquivados");
    } else {
      toast.success("Processo reativado");
      load();
    }
  };
  const excluirProcesso = async () => {
    setExcluindo(true);
    try {
      await http.delete(`/processos/${id}`);
      toast.success("Processo excluído");
      navigate("/processos");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui excluir o processo");
      setExcluindo(false); setExcluirOpen(false);
    }
  };
  const copiarNumero = async () => {
    const n = processo.numero || "";
    let ok = false;
    try { await navigator.clipboard.writeText(n); ok = true; } catch { /* tenta o caminho antigo abaixo */ }
    if (!ok) {
      try {
        const t = document.createElement("textarea"); t.value = n; t.style.position = "fixed"; t.style.opacity = "0";
        document.body.appendChild(t); t.select(); ok = document.execCommand("copy"); document.body.removeChild(t);
      } catch { ok = false; }
    }
    if (ok) { setCopiado(true); setTimeout(() => setCopiado(false), 1800); toast.success("Número copiado"); }
    else toast.error("Não consegui copiar; selecione o número e copie");
  };
  const cancelarEdicao = () => { fillForm(processo); setEditando(false); };

  const comAutos = processo.irmaos?.length > 0 || (processo.autos && processo.autos !== "principal");
  const metaAutos = autosMeta(processo.autos);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-4xl mx-auto pb-24 md:pb-8">
      <VoltarLink to="/processos" data-testid="voltar-link" className="text-xs font-semibold text-stone-500 hover:text-stone-900 flex items-center gap-1 mb-3 sm:mb-6 w-fit">
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Voltar
      </VoltarLink>

      <BannerComo />
      <header className="mb-4 sm:mb-6">
        {comAutos && (
          <div data-testid="autos-faixa" className={`rounded-md px-4 py-3 mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 ${metaAutos.faixa}`}>
            <div className="min-w-0">
              <div className="text-[10px] font-semibold tracking-[0.25em] uppercase opacity-80">Você está em</div>
              <div data-testid="autos-faixa-nome" className="font-display text-xl md:text-2xl font-bold uppercase tracking-wide leading-tight break-words">
                {autosLabel(processo.autos, processo.autos_nome)}
                {processo.autos !== "principal" && <span className="ml-2 text-xs font-semibold normal-case tracking-normal opacity-80">(apenso)</span>}
              </div>
            </div>
            {processo.irmaos?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="autos-barra">
                <span className="text-[10px] font-semibold uppercase tracking-wide opacity-80">Também:</span>
                {processo.irmaos.map((i) => (
                  <Link key={i.processo_id} to={`/processos/${i.processo_id}`} data-testid={`autos-irmao-${i.processo_id}`}
                    title={[i.vara, i.comarca].filter(Boolean).join(" · ") || "Abrir"}
                    className="inline-flex items-center min-h-[32px] px-2.5 rounded-full bg-white/20 hover:bg-white/30 text-[11px] font-semibold">
                    {autosLabel(i.autos, i.autos_nome)} ›
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
        {processo.arquivado && (
          <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-2" data-testid="processo-eyebrow">
            {processo.situacao_arquivo === "extinto" ? "Processo extinto · consulta pausada" : "Processo arquivado · consulta pausada"}
          </div>
        )}
        {processo.particular && <div className="mb-3"><BadgeParticular testid="processo-particular" /></div>}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <h1 data-testid="processo-titulo" className="font-display text-2xl md:text-3xl font-bold text-stone-900 break-words">{tituloFicha}</h1>
            {areaLabel && <p className="text-base md:text-lg text-stone-700 mt-0.5" data-testid="processo-acao">{areaLabel}</p>}
            <div className={`mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-3 gap-y-1.5 ${comAutos ? `border-l-8 pl-3 ${metaAutos.borda}` : ""}`}>
              <span className="inline-flex items-center gap-1.5 min-w-0">
                <span data-testid="processo-numero" className="font-display text-lg md:text-xl font-bold text-stone-900 tabular-nums break-all">{processo.numero}</span>
                <button type="button" data-testid="processo-copiar-numero" onClick={copiarNumero} aria-label="Copiar número do processo" title="Copiar número"
                  className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md text-stone-500 hover:text-stone-900 hover:bg-stone-100">
                  {copiado ? <Check className="w-4 h-4 text-emerald-700" strokeWidth={2.25} /> : <Copy className="w-4 h-4" strokeWidth={1.75} />}
                </button>
              </span>
              {processo.apelido && (
                <span data-testid="processo-apelido" title="Apelido" className="w-fit max-w-full break-words rounded-full border border-gold-300 bg-gold-50 px-3 py-0.5 text-sm font-semibold text-stone-800">{processo.apelido}</span>
              )}
            </div>
            {(processo.comarca || processo.vara) && (
              <p className="text-sm text-stone-500 mt-1" data-testid="processo-juizo-linha">
                {[processo.comarca, processo.vara].filter(Boolean).map((t) => nomeBonito(t)).join(" · ")}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1 sm:gap-1.5 lg:shrink-0 lg:justify-end" data-testid="processo-acoes">
            <Button type="button" size="sm" data-testid="agendar-no-processo" onClick={abrirAgendar}
              className="h-9 gap-1.5 px-2 sm:px-3 text-xs font-semibold bg-gold-500 text-black border border-gold-600 hover:bg-gold-400"
              title="Agendar prazo, audiência ou outro compromisso neste processo">
              <Plus className="w-3.5 h-3.5" strokeWidth={2.25} /> Novo evento
            </Button>
            <Button type="button" variant="outline" size="sm" data-testid="processo-detail-editar" onClick={editarDados}
              className="h-9 gap-1.5 px-2 sm:px-3 text-xs font-semibold border-stone-400 text-stone-900 bg-white hover:bg-stone-100"
              title="Editar apelido, cliente, partes, comarca, vara e outros dados">
              <Pencil className="w-3.5 h-3.5" strokeWidth={2} /> Editar
            </Button>
            {!deDono && (
              <Button asChild type="button" size="sm" variant="outline" className="h-9 gap-1.5 px-2 sm:px-3 text-xs text-stone-700">
                <Link data-testid="processo-enviar-copia" to={`/compartilhamentos?novo=processo&ref=${processo.processo_id}`} title="Compartilhar o processo com um parceiro externo (acompanhamento com comentários) ou enviar uma cópia fixa">
                  <Users className="w-3.5 h-3.5" strokeWidth={2} /> Compartilhar
                </Link>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm" data-testid="processo-mais" aria-label="Mais ações" className="h-9 w-9 p-0 text-stone-600">
                  <MoreHorizontal className="w-4 h-4" strokeWidth={2} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[230px]">
                <DropdownMenuItem data-testid="processo-editar-numero" onSelect={() => { setNovoNumero(processo.numero || ""); setNumeroOpen(true); }} className="gap-2 min-h-[40px]">
                  <Pencil className="w-4 h-4" strokeWidth={1.75} /> Editar número
                </DropdownMenuItem>
                {eu?.escritorio && !deDono && (
                  <DropdownMenuItem data-testid="particular-toggle"
                    onSelect={async () => {
                      const r = await marcarParticular("processo", [processo.processo_id], !processo.particular);
                      if (r?.alterados) setProcesso((p) => ({ ...p, particular: !p.particular }));
                    }}
                    className="gap-2 min-h-[40px]"
                    title={processo.particular ? "O escritório volta a ver este processo" : "O escritório deixa de ver este processo e os eventos dele"}>
                    <Lock className="w-4 h-4" strokeWidth={1.75} /> {processo.particular ? "Particular — voltar a compartilhar" : "Marcar como particular"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="processo-excluir" onSelect={() => setExcluirOpen(true)} className="gap-2 min-h-[40px] text-red-700 focus:text-red-800">
                  <Trash2 className="w-4 h-4" strokeWidth={1.75} /> Excluir processo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="processo-controles">
          <ResponsavelProcesso processo={processo} onTrocou={(novo) => setProcesso((p) => ({ ...p, ...novo }))} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="sm" data-testid="processo-situacao" title="Situação do processo" className={`h-8 gap-1.5 text-xs ${processo.arquivado ? "bg-stone-100 text-stone-500 border-stone-300" : "text-stone-700"}`}>
                <Archive className="w-3.5 h-3.5" strokeWidth={2} />
                {processo.arquivado ? (processo.situacao_arquivo === "extinto" ? "Extinto" : "Arquivado") : "Ativo"}
                <ChevronDown className="w-3 h-3" strokeWidth={2.5} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[230px]">
              {processo.arquivado && (
                <DropdownMenuItem data-testid="situacao-reativar" onSelect={() => mudarSituacao(false)} className="gap-2 min-h-[40px]">
                  <ArchiveRestore className="w-4 h-4" strokeWidth={1.75} /> Reativar (volta a consultar)
                </DropdownMenuItem>
              )}
              {!(processo.arquivado && processo.situacao_arquivo !== "extinto") && (
                <DropdownMenuItem data-testid="situacao-arquivar" onSelect={() => mudarSituacao(true, "arquivado")} className="gap-2 min-h-[40px]">
                  <Archive className="w-4 h-4" strokeWidth={1.75} /> Arquivar (pausa a consulta)
                </DropdownMenuItem>
              )}
              {!(processo.arquivado && processo.situacao_arquivo === "extinto") && (
                <DropdownMenuItem data-testid="situacao-extinto" onSelect={() => mudarSituacao(true, "extinto")} className="gap-2 min-h-[40px]">
                  <Archive className="w-4 h-4" strokeWidth={1.75} /> Marcar como extinto
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <Dialog open={excluirOpen} onOpenChange={(o) => { if (!excluindo) setExcluirOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="confirm-excluir-processo">
          <DialogHeader><DialogTitle className="font-display">Excluir este processo?</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600 leading-relaxed">
            O histórico de andamentos dele também é apagado. Os prazos e as audiências da agenda continuam. Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button variant="outline" data-testid="confirm-excluir-processo-cancelar" disabled={excluindo} onClick={() => setExcluirOpen(false)}>Cancelar</Button>
            <Button data-testid="confirm-excluir-processo-ok" disabled={excluindo} onClick={excluirProcesso} className="bg-rose-brand text-white hover:bg-rose-brand/90">
              {excluindo ? "Excluindo…" : "Excluir processo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <FilaClassificar processoId={id} total={processo.fila_total || 0} onChanged={() => { load(); reloadHist?.(); }} />
      <AutosDialog open={!!autosDlg} onOpenChange={(o) => { if (!o) setAutosDlg(null); }} base={processo} modo={autosDlg || "novo"}
        onSaved={(d) => { if (autosDlg === "novo" && d?.processo_id) navigate(`/processos/${d.processo_id}`); else load(); }} />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:grid-rows-[auto_auto_auto_1fr] gap-4 lg:gap-6 items-start" data-testid="ficha-grid">
        <div className="lg:col-start-1 lg:row-start-1 min-w-0">
          <div ref={dadosRef} className="bg-white border border-stone-200 rounded-md p-4 md:p-6" data-testid="processo-dados">
            <div className="flex items-center justify-between gap-2 mb-2 sm:mb-4">
              <h2 className="font-display text-sm font-bold tracking-widest uppercase text-stone-500">
                Dados do processo
              </h2>
            </div>
            {!editando && (
              <div data-testid="processo-detail-resumo">
                {/* Lote 85 — "capa do processo" (Modelo B validado pelo Robson): faixa do cliente + linhas rótulo/valor. */}
                {clienteNome ? (
                  <div className="hidden sm:block pb-3 mb-1 border-b border-stone-200" data-testid="resumo-cliente">   {/* no celular o cliente já é o título */}
                    <div className="text-[11px] text-stone-500">Cliente vinculado</div>
                    <div className="font-display text-base font-bold text-stone-900 break-words">{nomeBonito(clienteNome)}</div>
                  </div>
                ) : null}
                <div className="divide-y divide-dashed divide-stone-200">
                  {(processo.party_author || processo.party_defendant) && (
                    <div className="grid grid-cols-[96px_1fr] sm:grid-cols-[110px_1fr] gap-3 py-2" data-testid="resumo-partes">
                      <span className="text-xs text-stone-500">Partes</span>
                      <span className="text-sm text-stone-900 break-words">
                        {processo.party_author && (
                          <span>{nomeBonito(processo.party_author)} <span className="text-[11px] text-stone-400">({resumoLabels[0]})</span></span>
                        )}
                        {processo.party_author && processo.party_defendant && <span className="px-1.5 text-stone-400">×</span>}
                        {processo.party_defendant && (
                          <span>{nomeBonito(processo.party_defendant)} <span className="text-[11px] text-stone-400">({resumoLabels[1]})</span></span>
                        )}
                      </span>
                    </div>
                  )}
                  {!processo.party_author && !processo.party_defendant && !processo.arquivado && (clienteNome || areaLabel || processo.vara || processo.comarca) && (
                    <div className="grid grid-cols-[96px_1fr] sm:grid-cols-[110px_1fr] gap-3 py-2" data-testid="resumo-partes-aguardando">
                      <span className="text-xs text-stone-500">Partes</span>
                      <span className="text-xs text-stone-500">Completam sozinhas quando sair uma publicação deste processo (o DataJud não informa as partes).</span>
                    </div>
                  )}
                  {areaLabel && (
                    <div className="hidden sm:grid grid-cols-[96px_1fr] sm:grid-cols-[110px_1fr] gap-3 py-2" data-testid="resumo-tipo">
                      <span className="text-xs text-stone-500">Natureza</span>
                      <span>
                        <span className="inline-block border border-stone-200 rounded-full px-2.5 py-px text-xs bg-stone-50 text-stone-800">{areaLabel}</span>
                      </span>
                    </div>
                  )}
                  {(processo.vara || processo.comarca) && (
                    <div className="hidden sm:grid grid-cols-[96px_1fr] sm:grid-cols-[110px_1fr] gap-3 py-2" data-testid="resumo-juizo">
                      <span className="text-xs text-stone-500">Juízo</span>
                      <span className="text-sm text-stone-900 break-words">
                        {[processo.vara, processo.comarca ? (/comarca/i.test(processo.comarca) ? processo.comarca : `Comarca de ${processo.comarca}`) : ""]
                          .filter(Boolean).map((t) => nomeBonito(t)).join(" — ")}
                      </span>
                    </div>
                  )}
                  {processo.valor_causa && (
                    <div className="grid grid-cols-[96px_1fr] sm:grid-cols-[110px_1fr] gap-3 py-2" data-testid="resumo-valor">
                      <span className="text-xs text-stone-500">Valor da causa</span>
                      <span className="text-sm text-stone-900">{processo.valor_causa}</span>
                    </div>
                  )}
                  {processo.data_ajuizamento && (
                    <div className="grid grid-cols-[96px_1fr] sm:grid-cols-[110px_1fr] gap-3 py-2" data-testid="resumo-ajuiz">
                      <span className="text-xs text-stone-500">Ajuizamento</span>
                      <span className="text-sm text-stone-900 tabular-nums">{formatDate(processo.data_ajuizamento)}</span>
                    </div>
                  )}
                </div>
                {!clienteNome && !processo.party_author && !processo.party_defendant && !areaLabel &&
                  !processo.vara && !processo.comarca && !processo.valor_causa && !processo.data_ajuizamento && (
                  <p className="text-sm text-stone-500" data-testid="resumo-vazio">Nenhum dado complementar preenchido ainda. Vara, comarca, tipo e partes completam sozinhos quando o processo receber uma publicação ou uma consulta ao DataJud.</p>
                )}
                {processo.datajud_last_ok_at && (
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-stone-500" data-testid="resumo-consulta">
                    <Clock3 className="w-3.5 h-3.5 text-stone-400" strokeWidth={1.75} /> Última consulta: {formatDate(String(processo.datajud_last_ok_at).slice(0, 10))}
                  </p>
                )}
                {faltando.length > 0 && (
                  <p className="mt-3 text-xs text-stone-500" data-testid="resumo-faltando">
                    Adicionar:{" "}
                    {faltando.map((f, i) => (
                      <span key={f.key}>
                        <button
                          type="button"
                          onClick={() => setEditando(true)}
                          className="font-semibold text-emerald-brand hover:underline"
                        >
                          {f.label}
                        </button>
                        {i < faltando.length - 1 ? " · " : ""}
                      </span>
                    ))}
                  </p>
                )}
              </div>
            )}
            {editando && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-stone-500 mb-1.5 block">Apelido</Label>
                  <Input
                    data-testid="processo-detail-apelido"
                    value={apelido}
                    onChange={(e) => setApelido(e.target.value)}
                    placeholder="Ex: Ação Trabalhista - João"
                  />
                </div>
                <div>
                  <Label htmlFor="processo-detail-client" className="text-xs text-stone-500 mb-1.5 block">Cliente vinculado</Label>
                  <select
                    id="processo-detail-client"
                    data-testid="processo-detail-client"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-stone-300 bg-white text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-gold-500/30 focus:border-gold-500"
                  >
                    <option value="none">— Sem cliente —</option>
                    {clients.map((c) => (
                      <option key={c.client_id} value={c.client_id}>{toTitleCasePt(c.name)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-stone-500 mb-1.5 block">Comarca</Label>
                  <Input
                    data-testid="processo-detail-comarca"
                    value={comarca}
                    onChange={(e) => setComarca(e.target.value)}
                    placeholder="Ex: São Paulo"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-500 mb-1.5 block">Vara</Label>
                  <Input
                    data-testid="processo-detail-vara"
                    value={vara}
                    onChange={(e) => setVara(e.target.value)}
                    placeholder="Ex: 2ª Vara do Trabalho"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-stone-500 mb-1.5 block">Tipo de ação (define o rótulo das partes)</Label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {CASE_AREAS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      data-testid={`processo-detail-tipo-${value}`}
                      onClick={() => setCaseArea(value)}
                      className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                        caseArea === value
                          ? "bg-stone-900 text-white border-stone-900"
                          : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {caseArea === "outro" && (
                  <Input
                    data-testid="processo-detail-tipo-custom"
                    value={caseAreaCustom}
                    onChange={(e) => setCaseAreaCustom(e.target.value)}
                    placeholder="Especifique a área (ex: Tributário, Consumidor, Família…)"
                    className="mt-2"
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-stone-500 mb-1.5 block">{partyLabels[0]}</Label>
                  <Input
                    data-testid="processo-detail-party-author"
                    value={partyAuthor}
                    onChange={(e) => setPartyAuthor(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-500 mb-1.5 block">{partyLabels[1]}</Label>
                  <Input
                    data-testid="processo-detail-party-defendant"
                    value={partyDefendant}
                    onChange={(e) => setPartyDefendant(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-stone-500 mb-1.5 block">Valor da causa</Label>
                  <Input
                    data-testid="processo-detail-valor-causa"
                    value={valorCausa}
                    onChange={(e) => setValorCausa(maskCurrencyInput(e.target.value))}
                    placeholder="R$ 0,00"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-500 mb-1.5 block">Data de ajuizamento</Label>
                  <Input
                    data-testid="processo-detail-data-ajuizamento"
                    type="text"
                    inputMode="numeric"
                    placeholder="dd/mm/aaaa"
                    maxLength={10}
                    value={dataAjuizamentoText}
                    onChange={(e) => setDataAjuizamentoText(maskDateInput(e.target.value))}
                  />
                </div>
              </div>

              <Button
                data-testid="processo-detail-save"
                onClick={save}
                disabled={saving}
                className="gap-2 bg-emerald-brand text-white"
              >
                <Save className="w-4 h-4" strokeWidth={2} /> {saving ? "Salvando…" : "Salvar"}
              </Button>
              <Button
                data-testid="processo-detail-cancelar"
                type="button"
                variant="ghost"
                onClick={cancelarEdicao}
                disabled={saving}
                className="ml-2"
              >
                Cancelar
              </Button>
            </div>
            )}
          </div>
                  </div>
        <div className="lg:col-start-2 lg:row-start-1 lg:row-span-4 min-w-0" data-testid="ficha-historico">
          <div className="bg-white border border-stone-200 rounded-md p-4 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h3 className="font-display text-sm font-bold tracking-widest uppercase text-stone-500 flex items-center gap-2">
                <History className="w-4 h-4 text-stone-400" strokeWidth={1.75} /> Histórico
              </h3>
              <div className="flex flex-wrap items-center gap-2">
              <Button data-testid="resumir-movimentacoes" aria-label="Resumir movimentações" variant="outline" size="sm" onClick={() => gaveta.resumirProcesso(processo.processo_id || id, processo.apelido || processo.numero)} className="gap-1.5" title="O Assistente IA resume as últimas movimentações deste processo (somente leitura)">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> <span className="hidden sm:inline">Resumir movimentações</span>
              </Button>
              <Button data-testid="sync-processo-detail" aria-label="Atualizar histórico" variant="outline" size="sm" onClick={syncThis} disabled={syncingOne} className="gap-1.5" title="Consultar o DataJud só deste processo">
                <RefreshCw className={`w-3.5 h-3.5 ${syncingOne ? "animate-spin" : ""}`} strokeWidth={2} /> <span className="hidden sm:inline">{syncingOne ? "Atualizando…" : "Atualizar"}</span>
              </Button>
              <Button data-testid="paste-andamento-button" aria-label="Colar andamento" variant="outline" size="sm" onClick={() => setPasteOpen(true)} className="gap-1.5" title="Colar o e-mail de andamento do tribunal (e-SAJ PUSH, eproc, Projudi…)">
                <ClipboardPaste className="w-3.5 h-3.5" strokeWidth={2} /> <span className="hidden sm:inline">Colar andamento</span>
              </Button>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-andamento-button" size="sm" className="gap-1.5 bg-stone-900 hover:bg-stone-800 text-white" title="Registrar um andamento no histórico deste processo">
                    <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Registrar
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-display">Registrar andamento</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {processo.irmaos?.length > 0 && (
                      <div data-testid="add-andamento-autos">
                        <Label className="text-xs text-stone-500 mb-1.5 block">Em qual autos?</Label>
                        <div className="flex flex-wrap gap-1.5">
                          {[{ processo_id: id, autos: processo.autos, autos_nome: processo.autos_nome }, ...processo.irmaos].map((a) => {
                            const sel = (addAlvo || id) === a.processo_id;
                            return (
                              <button key={a.processo_id} type="button" data-testid={`add-andamento-autos-${a.processo_id}`} aria-pressed={sel}
                                onClick={() => setAddAlvo(a.processo_id)} className={`min-h-[36px] px-1 rounded-full ${sel ? "ring-2 ring-stone-500 ring-offset-1" : "opacity-80"}`}>
                                <AutosTag autos={a.autos} nome={a.autos_nome} className="py-1.5 px-2.5 text-[11px]" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-stone-500 mb-1.5 block">Data</Label>
                      <Input
                        data-testid="add-andamento-data"
                        type="text"
                        inputMode="numeric"
                        placeholder="dd/mm/aaaa"
                        maxLength={10}
                        value={addDataText}
                        onChange={(e) => setAddDataText(maskDateInput(e.target.value))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500 mb-1.5 block">Descrição</Label>
                      <Textarea
                        data-testid="add-andamento-descricao"
                        value={addDescricao}
                        onChange={(e) => setAddDescricao(e.target.value)}
                        placeholder="Ex: ligação com o cliente, decisão verbal em audiência…"
                        rows={3}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
                    <Button
                      data-testid="confirm-add-andamento"
                      onClick={addAndamento}
                      disabled={adding}
                      className="bg-emerald-brand text-white"
                    >
                      {adding ? "Salvando…" : "Adicionar"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            {/* Lote 120: filtros como abas de texto discretas (1 toque); "Limpar…" e "mostrar repetidos" ficam no menu ⋯. */}
            <div className="flex items-center gap-1 mb-3 border-b border-stone-200" data-testid="hist-filtros">
              <div className="flex flex-nowrap items-center gap-x-3 flex-1 min-w-0">
                {[["tudo", "Tudo"], ["externo", "Externo"], ["interno", "Interno"], ["revisoes", "Revisões"]].map(([k, label]) => (
                  <button key={k} type="button" data-testid={`hist-filtro-${k}`} onClick={() => setFiltroHist(k)} aria-pressed={filtroHist === k}
                    title={k === "externo" ? "Do tribunal (DJEN, DataJud, PUSH)" : k === "interno" ? "Minhas ações no app" : undefined}
                    className={`relative py-1.5 text-[11px] whitespace-nowrap font-semibold transition-colors ${filtroHist === k ? "text-stone-900 after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-stone-900" : "text-stone-400 hover:text-stone-700"}`}>
                    {label}
                  </button>
                ))}
              </div>
              {((!selMode && excluiveis.length > 0) || historico.some((a) => a.oculto_por_push)) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" data-testid="hist-menu" aria-label="Mais opções do histórico" className="p-1.5 rounded text-stone-400 hover:text-stone-800 hover:bg-stone-100">
                      <MoreHorizontal className="w-4 h-4" strokeWidth={2} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[220px]">
                    {!selMode && excluiveis.length > 0 && (
                      <DropdownMenuItem data-testid="hist-limpar" onSelect={() => setSelMode(true)} className="gap-2 text-xs">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} /> Limpar… (escolher andamentos para excluir)
                      </DropdownMenuItem>
                    )}
                    {historico.some((a) => a.oculto_por_push) && (
                      <DropdownMenuItem data-testid="toggle-hidden-datajud" onSelect={() => setShowHidden((v) => !v)} className="text-xs">
                        {showHidden ? "Esconder" : "Mostrar"} {historico.filter((a) => a.oculto_por_push).length} que repetem o PUSH
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {selMode && (
              <div data-testid="hist-selecao-barra" className="flex flex-wrap items-center gap-2 mb-3 p-2 rounded-md border border-rose-200 bg-rose-50">
                <span className="text-xs text-stone-700 mr-auto">
                  {selIds.size ? `${selIds.size} selecionado(s)` : "Marque os andamentos que quer excluir"}
                </span>
                <button type="button" data-testid="hist-sel-todos"
                  onClick={() => setSelIds(selIds.size === excluiveis.length ? new Set() : new Set(excluiveis.map(idDe)))}
                  className="text-xs font-semibold text-stone-700 underline">
                  {selIds.size === excluiveis.length ? "Limpar seleção" : `Selecionar todos (${excluiveis.length})`}
                </button>
                <Button type="button" size="sm" variant="outline" data-testid="hist-sel-cancelar" onClick={sairSelecao}>Cancelar</Button>
                <Button type="button" size="sm" data-testid="hist-sel-excluir" disabled={!selIds.size} onClick={() => setConfirmDel(true)}
                  className="bg-rose-brand text-white hover:bg-rose-brand/90">
                  Excluir{selIds.size ? ` (${selIds.size})` : ""}
                </Button>
              </div>
            )}
            {historico.filter((a) => a.origem !== "compartilhado").length === 0 ? (
              <Dica className="text-xs text-stone-500">{`Nenhum andamento ainda. Publicações do DJEN e movimentações do DataJud que citarem este processo entram aqui automaticamente.`}</Dica>
            ) : (
              <ol className="relative space-y-6 before:absolute before:left-[6px] before:top-3 before:bottom-3 before:w-px before:bg-stone-200" data-testid="hist-linha-do-tempo">
                {gruposHist.map(([dia, itens]) => (
                  <li key={dia} className="relative pl-7" data-testid={`hist-dia-${dia}`}>
                    <span aria-hidden className="absolute left-0 top-[3px] w-3.5 h-3.5 rounded-full border-2 border-stone-400 bg-white" />
                    <div className="flex items-baseline gap-2 mb-1.5">
                      <span className="text-xs font-bold text-stone-900 tabular-nums">{formatDate(dia)}</span>
                      {rotuloRelativo(dia) && <span className="text-[11px] text-stone-400" data-testid={`hist-rel-${dia}`}>{rotuloRelativo(dia)}</span>}
                    </div>
                    <ul className="space-y-3">
                      {itens.map((a) => {
                        const origem = a.sistema ? ORIGEM_LABEL.sistema : (ORIGEM_LABEL[a.origem] || ORIGEM_LABEL.manual);
                        const origemLabel = a.origem === "push" && a.sistema ? `PUSH · ${a.sistema}` : origem.label;
                        const interno = a.origem === "acao" || a.sistema === "Sistema";
                        return (
                          <li key={a.andamento_id || a.acao_id} data-testid={`andamento-${a.andamento_id || a.acao_id}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span aria-hidden className={`w-1.5 h-1.5 rounded-full ${origem.dot}`} />
                                <span className={`text-[11px] ${origem.text}`}>{origemLabel}</span>
                                {a.hora && <span className="text-[11px] text-stone-400 tabular-nums">· {a.hora}</span>}
                                {a.reconstruido && (
                                  <span className="text-[10px] text-stone-400" title="Reconstruído a partir dos dados antigos">(reconstruído)</span>
                                )}
                                {a.oculto_por_push && <span className="text-[10px] text-stone-400">(repete o PUSH)</span>}
                              </div>
                              {ehExcluivel(a) && (selMode ? (
                                <input
                                  type="checkbox"
                                  data-testid={`sel-andamento-${idDe(a)}`}
                                  checked={selIds.has(idDe(a))}
                                  onChange={() => toggleSel(idDe(a))}
                                  aria-label="Selecionar andamento"
                                  className="w-4 h-4 accent-rose-600"
                                />
                              ) : (
                                <span className="inline-flex items-center gap-2">
                                  {(a.origem === "manual" || a.origem === "colado") && (
                                    <button type="button" data-testid={`edit-andamento-${a.andamento_id}`} onClick={() => abrirEdicao(a)}
                                      title="Editar este andamento" aria-label="Editar andamento" className="text-stone-300 hover:text-stone-800 p-1 -m-1">
                                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    data-testid={`delete-andamento-${idDe(a)}`}
                                    onClick={() => delAndamento(idDe(a))}
                                    title="Excluir este andamento (dá para desfazer)"
                                    aria-label="Excluir andamento"
                                    className="text-stone-300 hover:text-rose-brand p-1 -m-1"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                                  </button>
                                </span>
                              ))}
                            </div>
                            <TextoRecolhivel testid={`andamento-texto-${a.andamento_id || a.acao_id}`} texto={a.descricao} className={interno ? "text-xs text-stone-500" : "text-sm text-stone-800"} />
                            {a.tem_texto && a.andamento_id && (
                              <PublicacaoCompleta processoId={id} andamentoId={a.andamento_id} testid={`andamento-pub-${a.andamento_id}`} />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </ol>
            )}
            {historico.some((a) => a.origem === "compartilhado") && (
              <div className="mt-5 border-t border-stone-200 pt-3" data-testid="compartilhamentos-faixa">
                <button type="button" data-testid="compartilhamentos-toggle" onClick={() => setSharesOpen((v) => !v)}
                  className="w-full flex items-center justify-between text-xs font-semibold text-sky-700 hover:text-sky-900">
                  <span>{historico.filter((a) => a.origem === "compartilhado").length} compartilhamento(s)</span>
                  <span aria-hidden>{sharesOpen ? "▲" : "▼"}</span>
                </button>
                {sharesOpen && (
                  <ul className="mt-2 space-y-2" data-testid="compartilhamentos-lista">
                    {historico.filter((a) => a.origem === "compartilhado").map((a) => (
                      <li key={a.andamento_id} className="text-xs text-stone-500 leading-relaxed">
                        <span className="font-semibold text-stone-900 tabular-nums mr-1.5">{formatDate(a.data)}</span>{a.descricao}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="lg:col-start-1 lg:row-start-2 min-w-0">
            <AcompanhamentoProcesso processo={processo} clients={clients} onAtualizar={syncThis} atualizando={syncingOne} reloadKey={acompKey} onChanged={reloadHist} />
                  </div>
        <div className="lg:col-start-1 lg:row-start-3 min-w-0">
          <AgendaProcesso processoId={id} deDono={deDono} meuId={eu?.user_id} reloadKey={acompKey} onNovo={abrirAgendar} />
        </div>
          <div className="lg:col-start-1 lg:row-start-4 min-w-0 bg-white border border-stone-200 rounded-md" data-testid="vinculos">
            <button type="button" data-testid="vinculos-toggle" aria-expanded={vincOpen} onClick={() => setVincOpen((v) => !v)}
              className="w-full flex items-center gap-2 min-h-[52px] px-4 md:px-5 text-left hover:bg-stone-50 rounded-md">
              <Link2 className="w-4 h-4 text-stone-500" strokeWidth={1.75} />
              <span className="flex-1 font-display text-base font-bold text-stone-900">Documentos e vínculos</span>
              {vincOpen ? <ChevronDown className="w-4 h-4 text-stone-500" /> : <ChevronRight className="w-4 h-4 text-stone-500" />}
            </button>
            <div hidden={!vincOpen} className="px-4 md:px-5 pb-4 space-y-3" data-testid="vinculos-corpo">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1.5">Outros autos deste número</p>
                {processo.irmaos?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {processo.irmaos.map((i) => (
                      <Link key={i.processo_id} to={`/processos/${i.processo_id}`} data-testid={`vinculo-autos-${i.processo_id}`}
                        className="inline-flex items-center min-h-[32px] px-2.5 rounded-full border border-stone-300 text-xs font-semibold text-stone-700 hover:border-stone-600">
                        {autosLabel(i.autos, i.autos_nome)} ›
                      </Link>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-1.5">
                  <button type="button" data-testid="autos-novo" onClick={() => setAutosDlg("novo")}
                    className="min-h-[32px] px-3 rounded-full border border-dashed border-stone-400 text-xs font-semibold text-stone-700 hover:border-stone-700 inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" strokeWidth={2} /> Outros autos
                  </button>
                  <button type="button" data-testid="autos-editar" onClick={() => setAutosDlg("editar")}
                    className="min-h-[32px] px-2 text-xs text-stone-500 hover:text-stone-900 underline underline-offset-2">Etiqueta</button>
                </div>
              </div>
              <p className="text-xs text-stone-400" data-testid="vinculos-documentos">Documentos e anexos do processo: em breve.</p>
            </div>
          </div>
      </div>
      {/* Lote 120: "+ Agendar" dentro do processo — Novo evento já com número, autos, cliente, vara, comarca e partes. */}
      <NewEventDialog
        hideTrigger
        open={agendarOpen}
        onOpenChange={setAgendarOpen}
        prefill={agendarPrefill}
        onCreated={() => { toast.success("Compromisso agendado neste processo"); reloadHist(); setAcompKey((k) => k + 1); }}
      />
      <Dialog open={!!editAnd} onOpenChange={(o) => { if (!o) setEditAnd(null); }}>
        <DialogContent data-testid="editar-andamento-dialog">
          <DialogHeader><DialogTitle className="font-display">Editar andamento</DialogTitle></DialogHeader>
          {editAnd && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-stone-500 mb-1.5 block">Data</Label>
                <Input data-testid="editar-andamento-data" inputMode="numeric" placeholder="dd/mm/aaaa" maxLength={10} value={editAnd.dataText}
                  onChange={(e) => setEditAnd({ ...editAnd, dataText: maskDateInput(e.target.value) })} />
              </div>
              <div>
                <Label className="text-xs text-stone-500 mb-1.5 block">Descrição</Label>
                <Textarea data-testid="editar-andamento-descricao" rows={4} value={editAnd.descricao} onChange={(e) => setEditAnd({ ...editAnd, descricao: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAnd(null)}>Cancelar</Button>
            <Button data-testid="editar-andamento-salvar" disabled={editBusy} onClick={salvarEdicao} className="bg-emerald-brand text-white">{editBusy ? "Salvando…" : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmDel} onOpenChange={setConfirmDel}>
        <DialogContent className="max-w-sm" data-testid="confirm-excluir-andamentos">
          <DialogHeader>
            <DialogTitle className="font-display">Excluir {selIds.size} andamento(s)?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600">Eles saem do histórico e não voltam nas próximas consultas ao DataJud/DJEN. Você terá alguns segundos para desfazer.</p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setConfirmDel(false)}>Voltar</Button>
            <Button data-testid="confirm-excluir-andamentos-ok" disabled={deleting} onClick={() => excluirAndamentos(Array.from(selIds))} className="bg-rose-brand text-white hover:bg-rose-brand/90">
              {deleting ? "Excluindo…" : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={pasteOpen} onOpenChange={(o) => { if (!o) closePaste(); }}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="paste-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Colar andamento</DialogTitle>
          </DialogHeader>
          <Dica className="text-xs text-stone-500">{`Cole o texto do e-mail de andamento do tribunal (e-SAJ PUSH, eproc, Projudi, PJe). O app lê as datas e os textos, mostra a prévia e só grava quando você confirmar.`}</Dica>
          <Textarea
            data-testid="paste-textarea"
            value={pasteText}
            onChange={(e) => { setPasteText(e.target.value); setPastePreview(null); }}
            placeholder="Cole aqui o e-mail inteiro…"
            rows={8}
            className="text-sm"
          />
          {pastePreview && (
            <div className="space-y-2" data-testid="paste-preview">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                {pastePreview.push ? (
                  <span className="inline-flex px-1.5 py-0.5 rounded border font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 border-emerald-200">
                    PUSH · {pastePreview.sistema}
                  </span>
                ) : (
                  <span className="inline-flex px-1.5 py-0.5 rounded border font-bold uppercase tracking-wide bg-amber-100 text-amber-700 border-amber-200">
                    Sistema não identificado
                  </span>
                )}
                <span className="text-stone-500">{pastePreview.entradas.length} andamento(s) encontrado(s)</span>
              </div>
              {pastePreview.avisos.map((a, i) => (
                <p key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="paste-aviso">{a}</p>
              ))}
              {pastePreview.push && (
                <p className="text-xs text-stone-500">Os andamentos do DataJud do mesmo dia e horário ficam ocultos no histórico (o PUSH traz o texto completo).</p>
              )}
              <ul className="space-y-2 max-h-56 overflow-y-auto">
                {pastePreview.entradas.map((e, i) => (
                  <li key={i} className={`text-xs border rounded p-2 ${e.duplicado ? "bg-stone-50 border-stone-200 text-stone-400" : "bg-white border-stone-200 text-stone-700"}`}>
                    <div className="font-semibold tabular-nums">
                      {formatDate(e.data)}{e.hora ? ` ${e.hora}` : ""}
                      {e.duplicado && <span className="ml-2 font-normal">já está no histórico</span>}
                    </div>
                    <div className="mt-0.5 leading-relaxed break-words">{e.texto}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closePaste}>Cancelar</Button>
            {!pastePreview ? (
              <Button data-testid="paste-read" onClick={readPaste} disabled={pasteBusy || pasteText.trim().length < 15} className="bg-emerald-brand text-white">
                {pasteBusy ? "Lendo…" : "Ler texto"}
              </Button>
            ) : pastePreview.numero_confere === false ? (
              <Button data-testid="paste-confirm-force" onClick={() => confirmPaste(true)} disabled={pasteBusy} variant="outline" className="border-amber-400 text-amber-800">
                Adicionar mesmo assim
              </Button>
            ) : (
              <Button data-testid="paste-confirm" onClick={() => confirmPaste(false)} disabled={pasteBusy || pastePreview.entradas.every((e) => e.duplicado)} className="bg-emerald-brand text-white">
                {pasteBusy ? "Salvando…" : "Adicionar ao histórico"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={numeroOpen} onOpenChange={setNumeroOpen}>
        <DialogContent className="max-w-md" data-testid="numero-dialog">
          <DialogHeader><DialogTitle>Editar número do processo</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="novo-numero">Número CNJ (20 dígitos)</Label>
            <Input id="novo-numero" data-testid="numero-input" inputMode="numeric" autoFocus value={novoNumero}
              onChange={(e) => setNovoNumero(e.target.value)} className="tabular-nums" placeholder="0000000-00.0000.0.00.0000"
              onKeyDown={(e) => { if (e.key === "Enter" && !salvandoNumero) salvarNumero(); }} />
            <Dica className="text-xs text-stone-500">{`As publicações e prazos ligados ao número antigo passam para o novo. As movimentações do DataJud são consultadas de novo.`}</Dica>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNumeroOpen(false)}>Cancelar</Button>
            <Button data-testid="numero-salvar" onClick={salvarNumero} disabled={salvandoNumero || novoNumero.replace(/\D/g, "").length !== 20} className="bg-emerald-brand text-white">
              {salvandoNumero ? "Salvando…" : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
